import type { MapRuntimePort } from './map-runtime';

const MAPTERHORN_TILE_URL = '/terrain/mapterhorn/{z}/{x}/{y}.webp';
const ESRI_SATELLITE_TILE_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const BAYERN_DOP20_TILE_URL = 'https://wmtsod1.bayernwolke.de/wmts/by_dop/smerc/{z}/{x}/{y}';

const TERRAIN_SOURCE_ID = 'mapterhorn-terrain';
const HILLSHADE_LAYER_ID = 'mapterhorn-hillshade';
const CONTOUR_SOURCE_ID = 'mapterhorn-contours-source';
const CONTOUR_LINE_ID = 'mapterhorn-contour-lines';
const CONTOUR_LABEL_ID = 'mapterhorn-contour-labels';

type TerrainLayerRuntime = MapRuntimePort;

export interface TerrainCamera {
  setTerrain3d(active: boolean, sourceId: string, exaggeration: number): void;
  restoreAtmosphere(): void;
}

export interface ContourDemSource {
  contourProtocolUrl(options: Record<string, unknown>): string;
  dispose?(): void;
}

export interface TerrainLayerState {
  satelliteActive: boolean;
  hillshade: boolean;
  terrain3d: boolean;
  contours: boolean;
  textFont: string[];
}

export interface TerrainLayerDependencies {
  camera: TerrainCamera;
  prepareContours?(): Promise<ContourDemSource>;
}

export interface TerrainLayerEvents {
  onUnavailable?(kind: 'contours', error?: unknown): void;
}

export interface TerrainLayerController {
  setEvents(events: TerrainLayerEvents): void;
  apply(state: TerrainLayerState): Promise<void>;
  dispose(): void;
}

type RegisterLayerGroup = (id: string, apply: () => void | Promise<void>) => () => void;

function firstSymbolLayerId(runtime: TerrainLayerRuntime) {
  return runtime.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id;
}

function removeLayer(runtime: TerrainLayerRuntime, id: string) {
  if (runtime.getLayer(id)) runtime.removeLayer(id);
}

function removeSource(runtime: TerrainLayerRuntime, id: string) {
  if (runtime.getSource(id)) runtime.removeSource(id);
}

function removeContours(runtime: TerrainLayerRuntime) {
  removeLayer(runtime, CONTOUR_LABEL_ID);
  removeLayer(runtime, CONTOUR_LINE_ID);
  removeSource(runtime, CONTOUR_SOURCE_ID);
}

function applySatellite(runtime: TerrainLayerRuntime, active: boolean) {
  if (!active) {
    removeLayer(runtime, 'esri-satellite');
    removeLayer(runtime, 'bayern-dop20');
    removeSource(runtime, 'esri-satellite-source');
    removeSource(runtime, 'bayern-dop20-source');
    return;
  }
  if (!runtime.getSource('esri-satellite-source')) runtime.addSource('esri-satellite-source', {
    type: 'raster',
    tiles: [ESRI_SATELLITE_TILE_URL],
    tileSize: 256,
    attribution: '<a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics, and the GIS User Community'
  });
  if (!runtime.getLayer('esri-satellite')) runtime.addLayer({
    id: 'esri-satellite',
    type: 'raster',
    source: 'esri-satellite-source',
    paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 }
  }, firstSymbolLayerId(runtime));
  if (!runtime.getSource('bayern-dop20-source')) runtime.addSource('bayern-dop20-source', {
    type: 'raster',
    tiles: [BAYERN_DOP20_TILE_URL],
    tileSize: 256,
    minzoom: 0,
    maxzoom: 19,
    bounds: [8.8, 47.2, 14.0, 50.6],
    attribution: '<a href="https://www.geodaten.bayern.de/">Bayerische Vermessungsverwaltung - www.geodaten.bayern.de</a>, <a href="https://creativecommons.org/licenses/by/4.0/deed.de">CC BY 4.0</a>'
  });
  if (!runtime.getLayer('bayern-dop20')) runtime.addLayer({
    id: 'bayern-dop20',
    type: 'raster',
    source: 'bayern-dop20-source',
    paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 }
  }, firstSymbolLayerId(runtime));
}

const hillshadePaint = {
  'hillshade-method': 'igor',
  'hillshade-exaggeration': 0.48,
  'hillshade-shadow-color': 'rgba(47,37,25,0.58)',
  'hillshade-highlight-color': 'rgba(255,248,222,0.28)',
  'hillshade-accent-color': 'rgba(112,82,44,0.26)',
  'hillshade-illumination-direction': 315
};

function paintHillshade(runtime: TerrainLayerRuntime, id: string) {
  Object.entries(hillshadePaint).forEach(([property, value]) => runtime.setPaintProperty(id, property, value));
}

function applyTerrain(runtime: TerrainLayerRuntime, state: TerrainLayerState, camera: TerrainCamera) {
  const visibility = state.hillshade ? 'visible' : 'none';
  const builtIn = Boolean(runtime.getLayer('hillshade'));
  const optional = Boolean(runtime.getLayer(HILLSHADE_LAYER_ID));
  if (builtIn) {
    runtime.setLayoutProperty('hillshade', 'visibility', visibility);
    paintHillshade(runtime, 'hillshade');
  }
  if (optional) {
    runtime.setLayoutProperty(HILLSHADE_LAYER_ID, 'visibility', visibility);
    paintHillshade(runtime, HILLSHADE_LAYER_ID);
  }
  const demSourceId = runtime.getSource('terrain') ? 'terrain' : TERRAIN_SOURCE_ID;
  if ((state.hillshade || state.terrain3d) && demSourceId === TERRAIN_SOURCE_ID && !runtime.getSource(TERRAIN_SOURCE_ID)) {
    runtime.addSource(TERRAIN_SOURCE_ID, {
      type: 'raster-dem',
      tiles: [MAPTERHORN_TILE_URL],
      minzoom: 0,
      maxzoom: 16,
      tileSize: 512,
      encoding: 'terrarium',
      attribution: '<a href="https://mapterhorn.com/attribution">© Mapterhorn</a>'
    });
  }
  if (state.hillshade && !builtIn && !optional) {
    runtime.addLayer({
      id: HILLSHADE_LAYER_ID,
      type: 'hillshade',
      source: demSourceId,
      paint: hillshadePaint
    }, firstSymbolLayerId(runtime));
  }
  camera.setTerrain3d(state.terrain3d, demSourceId, 2);
  if (!state.terrain3d) camera.restoreAtmosphere();
}

export function createTerrainLayerController(
  runtime: TerrainLayerRuntime,
  registerLayerGroup: RegisterLayerGroup,
  dependencies: TerrainLayerDependencies,
  initialEvents: TerrainLayerEvents = {}
): TerrainLayerController {
  let events = initialEvents;
  let desiredState: TerrainLayerState | null = null;
  let contourSource: ContourDemSource | null = null;
  let contourPromise: Promise<ContourDemSource> | null = null;
  let disposed = false;
  let revision = 0;
  let contourDisposed = false;

  const disposeContourSource = (source: ContourDemSource | null) => {
    if (!source || contourDisposed) return;
    contourDisposed = true;
    source.dispose?.();
  };

  const getContourSource = () => {
    if (contourSource) return Promise.resolve(contourSource);
    if (!dependencies.prepareContours) return Promise.reject(new Error('Contour plugin unavailable'));
    contourPromise ||= dependencies.prepareContours().then((source) => {
      contourSource = source;
      if (disposed) disposeContourSource(source);
      return source;
    }).catch((error) => {
      contourPromise = null;
      throw error;
    });
    return contourPromise;
  };

  const applyContours = async (state: TerrainLayerState, applyRevision: number) => {
    if (!state.contours) {
      removeContours(runtime);
      return;
    }
    try {
      const source = await getContourSource();
      if (disposed || applyRevision !== revision || desiredState !== state || !runtime.isStyleLoaded()) return;
      if (!runtime.getSource(CONTOUR_SOURCE_ID)) runtime.addSource(CONTOUR_SOURCE_ID, {
        type: 'vector',
        tiles: [source.contourProtocolUrl({
          thresholds: { 10: [200, 1000], 12: [100, 500], 14: [20, 100] },
          elevationKey: 'ele',
          levelKey: 'level',
          contourLayer: 'contours',
          buffer: 1,
          overzoom: 2
        })],
        maxzoom: 17
      });
      const before = runtime.getLayer('water') ? 'water' : firstSymbolLayerId(runtime);
      if (!runtime.getLayer(CONTOUR_LINE_ID)) runtime.addLayer({
        id: CONTOUR_LINE_ID,
        type: 'line',
        source: CONTOUR_SOURCE_ID,
        'source-layer': 'contours',
        filter: ['>', ['get', 'ele'], 0],
        paint: {
          'line-color': 'rgb(164, 130, 71)',
          'line-width': ['match', ['get', 'level'], 1, 1.15, 0.55],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0.45, 14, 0.85]
        }
      }, before);
      if (!runtime.getLayer(CONTOUR_LABEL_ID)) runtime.addLayer({
        id: CONTOUR_LABEL_ID,
        type: 'symbol',
        source: CONTOUR_SOURCE_ID,
        'source-layer': 'contours',
        filter: ['all', ['==', ['get', 'level'], 1], ['>', ['get', 'ele'], 0]],
        paint: {
          'text-color': 'rgb(154, 131, 93)',
          'text-halo-color': 'rgba(227, 228, 210, 0.95)',
          'text-halo-width': 2
        },
        layout: {
          'symbol-placement': 'line',
          'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 15, 12],
          'text-field': ['concat', ['number-format', ['get', 'ele'], {}], 'm'],
          'text-font': state.textFont
        }
      }, before);
      else runtime.setLayoutProperty(CONTOUR_LABEL_ID, 'text-font', state.textFont);
    } catch (error) {
      if (!disposed && applyRevision === revision) events.onUnavailable?.('contours', error);
    }
  };

  const applyDesired = async () => {
    const state = desiredState;
    if (!state || disposed || !runtime.isStyleLoaded()) return;
    const applyRevision = ++revision;
    applySatellite(runtime, state.satelliteActive);
    applyTerrain(runtime, state, dependencies.camera);
    await applyContours(state, applyRevision);
  };

  const unregister = registerLayerGroup('terrain', applyDesired);

  return {
    setEvents(nextEvents) { events = nextEvents; },
    apply(state) {
      desiredState = state;
      return applyDesired();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      revision += 1;
      unregister();
      removeContours(runtime);
      disposeContourSource(contourSource);
    }
  };
}
